import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IMAGES } from "../../constants/image";
import { useAuth } from "../../context/AuthContext";

type HeaderProps = {
  pageShortName?: string;
  tickerContent?: string;
};

const Header: React.FC<HeaderProps> = ({
  pageShortName = "header_ticker",
  tickerContent = "",
}) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [userInitial, setUserInitial] = useState<string>("U");
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, userDetails, logout, lastLoginAt } = useAuth();

  const getDisplayValue = (value: unknown, fallback = "Not available") => {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }

    if (typeof value === "number") {
      return String(value);
    }

    return fallback;
  };

  const parseDateOnly = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const ddmmyyyy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ddmmyyyy) {
      const [, dd, mm, yyyy] = ddmmyyyy;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }

    const iso = trimmed.includes("T") ? trimmed : trimmed.replace(/\s+/g, "T");
    const parsed = new Date(iso);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    const slashNormalized = trimmed.replace(/-/g, "/");
    const altParsed = new Date(slashNormalized);
    if (!Number.isNaN(altParsed.getTime())) {
      return altParsed;
    }

    return null;
  };

  const parseLoginTimestamp = (value: unknown): Date | null => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number") {
      const numeric = value;
      return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;

      if (/^\d{10}$/.test(trimmed)) {
        return new Date(Number(trimmed) * 1000);
      }

      if (/^\d{13}$/.test(trimmed)) {
        return new Date(Number(trimmed));
      }

      const parsedDate = parseDateOnly(trimmed);
      if (parsedDate) {
        return parsedDate;
      }
    }

    return null;
  };

  const formatTimeOnly = (value: string): string => {
    const trimmed = value.trim();
    const match = trimmed.match(
      /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([APap][Mm])?$/,
    );
    if (!match) {
      return trimmed;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] || "0");
    const ampm = match[4];
    const normalizedHours = ampm
      ? ampm.toUpperCase() === "PM"
        ? (hours % 12) + 12
        : hours % 12
      : hours;

    const date = new Date(1970, 0, 1, normalizedHours, minutes, seconds);
    return new Intl.DateTimeFormat("en", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const formatLoginDateTime = (value: string | null) => {
    if (!value) {
      return { date: "Not available", time: "Not available" };
    }

    const parsedDate = parseLoginTimestamp(value);
    if (parsedDate) {
      return {
        date: new Intl.DateTimeFormat("en", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(parsedDate),
        time: new Intl.DateTimeFormat("en", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }).format(parsedDate),
      };
    }

    const trimmed = value.trim();
    const [datePart, ...otherParts] = trimmed.split(" ");
    const timePart = otherParts.join(" ").trim();
    return {
      date: datePart || value,
      time: timePart || "Not available",
    };
  };

  const buildLoginDetails = (details: any) => {
    if (!details) {
      return { date: "Not available", time: "Not available" };
    }

    const dateValue =
      details?.last_login_date ||
      details?.last_login ||
      details?.lastLogin ||
      details?.last_login_at;
    const timeValue =
      details?.last_login_time || details?.login_time || details?.loginTime;

    if (dateValue && timeValue) {
      const parsedDate = parseDateOnly(String(dateValue));
      const formattedDate = parsedDate
        ? new Intl.DateTimeFormat("en", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }).format(parsedDate)
        : String(dateValue).trim();

      return {
        date: formattedDate,
        time: formatTimeOnly(String(timeValue)),
      };
    }

    const fallbackValue =
      details?.last_login ||
      details?.lastLogin ||
      details?.last_login_at ||
      details?.login_time ||
      details?.loginTime ||
      details?.last_login_date ||
      details?.last_login_time ||
      lastLoginAt ||
      null;

    return formatLoginDateTime(fallbackValue ? String(fallbackValue) : null);
  };

  const loginDetails = buildLoginDetails(userDetails);

  const displayName = getDisplayValue(
    userDetails?.name ||
      userDetails?.full_name ||
      userDetails?.user_name ||
      "User",
    "User",
  );
  const displayEmail = getDisplayValue(
    userDetails?.email || userDetails?.email_address || "",
    "Email not available",
  );

  const ICON_MAP: Record<string, string> = {
    "wia-Logo": IMAGES.HEADER.WIA_LOGO,
    "bagchi-Logo": IMAGES.HEADER.BAGCHI_LOGO,
    "search-icon": IMAGES.HEADER.SEARCH_ICON,
    "user-icon": IMAGES.PROFILE.USER_PROFILE,
    "edit-icon": IMAGES.ICONS.EDIT,
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const closeNav = () => {
    setIsNavOpen(false);
  };

  useEffect(() => {
    const firstLetter = (displayName || "U")
      .toString()
      .trim()
      .charAt(0)
      .toUpperCase();
    setUserInitial(firstLetter || "U");
    setIsUserMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname, displayName]);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const closeUserMenu = () => setIsUserMenuOpen(false);
    document.addEventListener("click", closeUserMenu);
    return () => document.removeEventListener("click", closeUserMenu);
  }, [isUserMenuOpen]);

  const toggleUserMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsUserMenuOpen((prev) => !prev);
  };

  const navLinkClass = (path: string) =>
    `nav-link${location.pathname === path ? " active" : ""}`;

  const handleMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    closeNav();
    navigate("/login");
  };

  const searchTargets = [
    {
      path: "/",
      label: "Home",
      description: "Childhood cancer overview, mission, vision, and values",
      terms: [
        "home",
        "childhood cancer",
        "mission",
        "vision",
        "values",
        "logo",
      ],
    },
    {
      path: "/factsheets",
      label: "Factsheets",
      description: "Generate and download PBCCR factsheet reports",
      terms: [
        "factsheet",
        "factsheets",
        "fact sheet",
        "download",
        "report",
        "filters",
      ],
    },
    {
      path: "/factsheets",
      label: "Download Factsheet",
      description: "Go to the factsheet filter and download section",
      terms: [
        "download",
        "download factsheet",
        "download report",
        "factsheet button",
      ],
    },
    {
      path: "/data-visualization",
      label: "Data Visualization",
      description:
        "Explore registry data, tables, filters, and incidence summaries",
      terms: [
        "data",
        "visualization",
        "grid",
        "table",
        "incidence",
        "statistics",
        "filters",
      ],
    },
    {
      path: "/methods",
      label: "Methods",
      description:
        "Review methods, data collection, calculations, and formulas",
      terms: ["methods", "method", "calculation", "formula", "data collection"],
    },
    {
      path: "/initiatives",
      label: "Initiatives",
      description: "View partner organizations and PBCCR initiatives",
      terms: ["initiatives", "partners", "stakeholders", "collaboration"],
    },
    {
      path: "/about",
      label: "About",
      description: "Read about Cancer Institute, PBCCR, team, and staff",
      terms: [
        "about",
        "pbccr",
        "team",
        "staff",
        "cancer institute",
        "wia",
        "sponsors",
        "bagchi",
      ],
    },
    {
      path: "/about",
      label: "PBCCR Team and Staff",
      description: "View PBCCR team, staff, stakeholders, and sponsors",
      terms: ["team", "staff", "stakeholder", "sponsor", "bagchi"],
    },
    {
      path: "/help",
      label: "Help",
      description: "Find support information and frequently asked questions",
      terms: ["help", "support", "faq", "question", "how to use"],
    },
    {
      path: "/help",
      label: "FAQs",
      description: "Open Help to read frequently asked questions",
      terms: ["faq", "faqs", "questions", "answers"],
    },
    {
      path: "/contact-us",
      label: "Contact Us",
      description: "PBCCR address, email, and website details",
      terms: ["contact", "email", "address", "pbccr email", "support email"],
    },
    {
      path: "/terms-of-use",
      label: "Terms of Use",
      description: "Read website terms and conditions",
      terms: ["terms", "terms of use"],
    },
    {
      path: "/privacy-policy",
      label: "Privacy Policy",
      description: "Read privacy and data-use information",
      terms: ["privacy", "privacy policy"],
    },
    {
      path: "/forgot-password",
      label: "Forgot Password",
      description: "Reset a forgotten password",
      terms: ["forgot password", "reset password", "password help"],
    },
    ...(isLoggedIn
      ? [
          {
            path: "/profile",
            label: "Profile",
            description: "Update your profile details",
            terms: ["profile", "account", "user menu", "details"],
          },
          {
            path: "/change-password",
            label: "Change Password",
            description: "Change your account password",
            terms: ["change password", "password", "user menu"],
          },
        ]
      : [
          {
            path: "/login",
            label: "Login",
            description: "Sign in to your PBCCR account",
            terms: ["login", "signin", "sign in", "account"],
          },
          {
            path: "/signup",
            label: "Signup",
            description: "Create a new PBCCR account",
            terms: ["signup", "sign up", "register", "create account"],
          },
        ]),
  ];

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchSuggestions = normalizedSearchQuery
    ? searchTargets
        .filter((target) =>
          [target.label, target.description, ...target.terms].some((term) =>
            term.toLowerCase().includes(normalizedSearchQuery),
          ),
        )
        .slice(0, 6)
    : [];

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchQuery(event.target.value);
    setIsSearchOpen(true);
  };

  const goToSearchTarget = (path: string) => {
    navigate(path);
    setSearchQuery("");
    setIsSearchOpen(false);
    closeNav();
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (searchSuggestions.length === 1) {
      goToSearchTarget(searchSuggestions[0].path);
    } else if (searchSuggestions.length > 1) {
      setIsSearchOpen(true);
    }
  };

  const renderSearchSuggestions = () =>
    isSearchOpen && normalizedSearchQuery ? (
      <div className="header-search-suggestions" role="listbox">
        {searchSuggestions.length > 0 ? (
          searchSuggestions.map((target) => (
            <button
              type="button"
              key={`${target.label}-${target.path}`}
              className="header-search-suggestion"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => goToSearchTarget(target.path)}
              role="option"
              aria-selected="false"
            >
              <span>{target.label}</span>
              <small>{target.description}</small>
            </button>
          ))
        ) : (
          <div className="header-search-empty">No matching pages</div>
        )}
      </div>
    ) : null;

  return (
    <header className="pbccr-header">
      <div className="container-fluid px-0">
        {/* Header Top Section with Logos and Title */}
        <div className="header-top py-2 py-md-3">
          <div className="row align-items-center justify-content-between g-2 g-md-3">
            {/* Logo 1 - Left */}
            <div className="col-2 col-md-1 d-flex justify-content-start ps-2 ps-md-3">
              <Link
                to="/"
                className="header-logo-link header-logo-link-left"
                aria-label="Go to home page"
              >
                <img
                  src={ICON_MAP["wia-Logo"]}
                  alt="Cancer Institute Logo"
                  className="header-logo"
                  width="96"
                  height="96"
                  decoding="async"
                />
              </Link>
            </div>

            {/* Title - Center */}
            <div className="col-8 col-md d-flex flex-column justify-content-center align-items-center text-center">
              <h1 className="header-title mb-0">
                <span className="header-title-purple">
                  CANCER INSTITUTE (WIA) - BAGCHI TNPBCCR
                </span>
              </h1>
              <h2 className="header-subtitle mb-0">
                (Tamil Nadu Population-Based Childhood Cancer Registry)
              </h2>
            </div>

            {/* Logo 2 - Right */}
            <div className="col-2 col-md-1 d-flex justify-content-end pe-2 pe-md-3">
              <Link
                to="/"
                className="header-logo-link header-logo-link-right"
                aria-label="Go to home page"
              >
                <img
                  src={ICON_MAP["bagchi-Logo"]}
                  alt="PBCCR Logo"
                  className="header-logo"
                  width="96"
                  height="96"
                  decoding="async"
                />
              </Link>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <nav className="navbar navbar-expand-lg header-navbar p-0">
          <div className="container-fluid px-0">
            {/* Mobile Toggle Button */}
            <button
              className="navbar-toggler border-0"
              type="button"
              onClick={toggleNav}
              aria-controls="navbarNav"
              aria-expanded={isNavOpen}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Navbar Items */}
            <div
              className={`collapse navbar-collapse ${isNavOpen ? "show" : ""}`}
              id="navbarNav"
            >
              <ul className="navbar-nav me-auto mb-lg-0">
                <li className="nav-item">
                  <Link className={navLinkClass("/")} to="/" onClick={closeNav}>
                    HOME
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/factsheets")}
                    to="/factsheets"
                    onClick={closeNav}
                  >
                    FACTSHEET
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/data-visualization")}
                    to="/data-visualization"
                    onClick={closeNav}
                  >
                    DATA VISUALIZATION
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/methods")}
                    to="/methods"
                    onClick={closeNav}
                  >
                    METHODS
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/initiatives")}
                    to="/initiatives"
                    onClick={closeNav}
                  >
                    INITIATIVES
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/about")}
                    to="/about"
                    onClick={closeNav}
                  >
                    ABOUT
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    className={navLinkClass("/help")}
                    to="/help"
                    onClick={closeNav}
                  >
                    HELP
                  </Link>
                </li>
              </ul>

            </div>

            {/* Search and Login - Desktop */}
            <div className="d-flex gap-2 align-items-center ms-auto header-right-controls">
              <form
                className="d-none d-lg-flex align-items-center header-search-form"
                role="search"
                onSubmit={handleSearchSubmit}
              >
                <input
                  className="form-control"
                  type="search"
                  placeholder="Search resources..."
                  aria-label="Search"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => setIsSearchOpen(true)}
                  onBlur={() => setIsSearchOpen(false)}
                />
                <button
                  type="submit"
                  className="search-icon"
                  aria-label="Search"
                >
                  <img
                    src={ICON_MAP["search-icon"]}
                    alt="Search Icon"
                    className="search-icon-img"
                  />
                </button>
                {renderSearchSuggestions()}
              </form>
              {isLoggedIn ? (
                  <div className="header-user-menu" onClick={handleMenuClick}>
                    <button
                      type="button"
                      className="header-user-btn"
                      onClick={toggleUserMenu}
                      aria-label="Open user menu"
                    >
                      {/* <span className="header-user-avatar">{userInitial}</span> */}
                      <img src={ICON_MAP["user-icon"]} alt="User Icon" />
                    </button>

                    {isUserMenuOpen && (
                      <div className="header-user-dropdown">
                        <div className="header-user-profile-card">
                          <div className="header-user-profile-avatar">
                            {userInitial}
                            <Link
                              className="header-user-avatar-edit"
                              to="/profile"
                              onClick={closeNav}
                              aria-label="Edit profile"
                            >
                              <img
                                src={ICON_MAP["edit-icon"]}
                                alt="Edit Icon"
                              />
                            </Link>
                          </div>
                          <div className="header-user-profile-info">
                            <div className="header-user-profile-name">
                              {displayName}
                            </div>
                            <div className="header-user-profile-email">
                              {displayEmail}
                            </div>
                            <div className="header-user-profile-last-login">
                              Last login: {loginDetails.date} at{" "}
                              {loginDetails.time}
                            </div>
                            {/* <div className="header-user-profile-address">{displayAddress}</div> */}
                            {/* <div className="header-user-profile-meta">
                              <div className="header-user-profile-meta-item">
                                <span className="header-user-profile-label">
                                  Last login
                                </span>
                                <span className="header-user-profile-value">
                                  {loginDetails.date}
                                </span>
                              </div>
                              <div className="header-user-profile-meta-item">
                                <span className="header-user-profile-label">
                                  Login time
                                </span>
                                <span className="header-user-profile-value">
                                  {loginDetails.time}
                                </span>
                              </div>
                            </div> */}
                          </div>
                        </div>
                        {/* <div className="header-user-dropdown-links">
                          <Link to="/profile" onClick={closeNav}>
                            Profile
                          </Link>
                          <Link to="/change-password" onClick={closeNav}>
                            Change Password
                          </Link>
                          <button type="button" onClick={handleLogout}>
                            Logout
                          </button>
                        </div> */}
                        <div className="d-flex gap-2 header-user-button-group">
                          <Link
                            className="btn btn-outline w-100 profilecard-action-row"
                            to="/change-password"
                            onClick={closeNav}
                          >
                            Change Password
                          </Link>
                          <button
                            className="btn btn-danger w-100 profilecard-action-row"
                            type="button"
                            onClick={handleLogout}
                          >
                            Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
              ) : (
                <Link
                  to="/login"
                  className="btn btn-login"
                  onClick={closeNav}
                >
                  LOGIN/SIGNUP
                </Link>
              )}
            </div>
          </div>
        </nav>

        {/* Mobile Search - Visible on small screens */}
        <div className="d-lg-none py-2 pb-3">
          <form
            className="d-flex align-items-center header-search-form-mobile"
            role="search"
            onSubmit={handleSearchSubmit}
          >
            <input
              className="form-control"
              type="search"
              placeholder="Search resources..."
              aria-label="Search"
              value={searchQuery}
              onChange={handleSearchInputChange}
              onFocus={() => setIsSearchOpen(true)}
              onBlur={() => setIsSearchOpen(false)}
            />
            <button type="submit" className="search-icon" aria-label="Search">
              <img
                src={ICON_MAP["search-icon"]}
                alt="Search Icon"
                className="search-icon-img"
              />
            </button>
            {renderSearchSuggestions()}
          </form>
        </div>

        {/* Sponsored By */}
        <div className="header-sponsored text-purple py-1">
          <div className="header-sponsored-track">
            {tickerContent ? (
              <span dangerouslySetInnerHTML={{ __html: tickerContent }} />
            ) : (
              <span>
                Sponsored By <span className="fw-bold">BAGCHI Foundation</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
